// Client-side search results caching

/**
 * Search results cache (client-side)
 * @type {Map<string, {data: Object, timestamp: number}>}
 */
const searchResultsCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Generate cache key from query and filters
 * @param {string} query - Search query
 * @param {number} page - Page number
 * @param {Object} filters - Search filters
 * @returns {string} Cache key
 */
export function getCacheKey(query, page, filters = {}) {
    const filterStr = JSON.stringify(filters);
    return `${query}:${page}:${filterStr}`;
}

/**
 * Get cached search results
 * @param {string} query - Search query
 * @param {number} page - Page number
 * @param {Object} filters - Search filters
 * @returns {Object|null} Cached results or null
 */
export function getCachedResults(query, page, filters = {}) {
    try {
        const key = getCacheKey(query, page, filters);
        const cached = searchResultsCache.get(key);
        if (!cached) return null;
        
        const age = Date.now() - cached.timestamp;
        if (age > CACHE_TTL_MS) {
            searchResultsCache.delete(key);
            return null;
        }
        
        return cached.data;
    } catch(_) {
        return null;
    }
}

/**
 * Cache search results
 * @param {string} query - Search query
 * @param {number} page - Page number
 * @param {Object} filters - Search filters
 * @param {Object} data - Results data
 */
export function cacheResults(query, page, filters = {}, data) {
    try {
        const key = getCacheKey(query, page, filters);
        searchResultsCache.set(key, {
            data: data,
            timestamp: Date.now()
        });
        
        // Limit cache size to 50 entries
        if (searchResultsCache.size > 50) {
            const firstKey = searchResultsCache.keys().next().value;
            searchResultsCache.delete(firstKey);
        }
    } catch(_) {}
}

